import { DURATION } from '../constants/couponConstants';
import { COUPON_TYPES } from 'aa/vue/models/CouponDiscount';
import { decimalFormat } from 'aa/vue/components/account/helpers';
import { FLOW_TYPE } from 'models/FlowModel';

const AARP_SYMBOL = '§';

export function getPromoString(
    coupon,
    discountedPrice,
    regularPrice,
    term,
    shortenedTerm,
    currency,
    isAnnualPlan,
    isFTC,
    hasTrial,
) {
    const offerPriceString = getOfferPriceString(
        currency,
        discountedPrice,
        term,
        shortenedTerm,
        coupon,
        isFTC,
        isAnnualPlan,
    );
    const durationString = getDurationString(coupon, term, isAnnualPlan, isFTC);
    const afterPromoString = getAfterPromoString(
        coupon,
        currency,
        regularPrice,
        shortenedTerm,
        isFTC,
        hasTrial,
    );

    return `${offerPriceString}${durationString}${afterPromoString}`;
}

export function getOfferPriceString(
    currency,
    discountedPrice,
    term,
    shortenedTerm,
    coupon,
    isFTC,
    isAnnualPlan,
    subunits = 100,
) {
    let adjustedDiscountedPrice = discountedPrice;
    if (coupon?.discount?.type === COUPON_TYPES.PERCENT && coupon?.discount?.rate === 1) {
        adjustedDiscountedPrice = '0';
    }

    const alreadyPrettyPrinted = adjustedDiscountedPrice?.toString()?.includes(currency);
    if (!alreadyPrettyPrinted) {
        adjustedDiscountedPrice = decimalFormat(parseFloat(adjustedDiscountedPrice), subunits);
    }

    const offerPriceString = alreadyPrettyPrinted
        ? adjustedDiscountedPrice
        : `${currency}${adjustedDiscountedPrice}`;
    const lowercaseTerm = term.toLowerCase();

    switch (coupon?.duration) {
        case DURATION.TEMPORAL:
            const oneCadenceBasedOnPlanType = getSingleCadenceAmount(
                coupon?.temporal_unit,
                isAnnualPlan,
            );
            const termDisplay =
                coupon.temporal_amount > oneCadenceBasedOnPlanType
                    ? `/${isFTC ? shortenedTerm : lowercaseTerm}`
                    : `${isFTC ? '' : `/${lowercaseTerm}`}`;
            return `${offerPriceString}${termDisplay}`;
        case DURATION.SINGLE_USE:
            return offerPriceString;
        case DURATION.FOREVER:
            return `${offerPriceString}/${lowercaseTerm}${isAarpCoupon(coupon.code) ? ' ' + AARP_SYMBOL : ''}`;
        default:
            return `${offerPriceString}/${lowercaseTerm}`;
    }
}

export function getDurationString(coupon, term, isAnnualPlan, isFTC) {
    const durationString = isFTC ? ' for the first' : ' for';
    let finalTerm = term.toLowerCase();

    switch (coupon?.duration) {
        case DURATION.SINGLE_USE:
            return `${durationString}${isFTC ? '' : ' 1'} ${finalTerm}`;
        case DURATION.TEMPORAL:
            const temporalAmount = getTemporalAmount(coupon, isAnnualPlan);
            if (coupon?.temporal_unit === 'day') {
                finalTerm = 'day';
            }
            if (temporalAmount === 1) {
                return `${durationString} ${isFTC ? '' : '1 '}${finalTerm}`;
            }
            return `${durationString} ${temporalAmount} ${finalTerm}s`;
        // forever coupons have no duration string
        case DURATION.FOREVER:
        default:
            return ``;
    }
}

export function getAfterPromoString(
    coupon,
    currency,
    regularPrice,
    shortenedTerm,
    isFTC,
    hasTrial,
) {
    const afterTrialCopy = ' after trial';
    if (!isFTC) {
        return (coupon?.duration === DURATION.FOREVER && hasTrial) || (!coupon && hasTrial)
            ? afterTrialCopy
            : '';
    }

    if (!coupon?.duration) {
        return hasTrial ? afterTrialCopy : '';
    }

    const formattedPriceWithCadence = `${currency}${regularPrice}/${shortenedTerm}`;

    switch (coupon?.duration) {
        case DURATION.FOREVER:
            return ` instead of ${formattedPriceWithCadence}${hasTrial ? afterTrialCopy : ''}`;

        case DURATION.SINGLE_USE:
        case DURATION.TEMPORAL:
        default:
            return ` then ${formattedPriceWithCadence}`;
    }
}

export function getTemporalAmount(coupon, isAnnualPlan, couponLength = null) {
    const temporalAmount = couponLength || coupon.temporal_amount;
    if (coupon.temporal_unit === 'month' && isAnnualPlan) {
        return Math.ceil(temporalAmount / 12); // Round up to the nearest whole year
    }

    return temporalAmount;
}

export function checkForCouponErrors(coupon, countryCode, currencyCode, freeTrialBlocked = true) {
    let containsError = false;
    if (coupon.regionCode != null && coupon.regionCode.toUpperCase() !== countryCode) {
        containsError = 'd';
    } else if (coupon.discount.type == 'free_trial' && freeTrialBlocked) {
        containsError = 'a';
    } else if (coupon.discount.type == 'dollars') {
        if (
            coupon.discount.amount[currencyCode] == 0 ||
            coupon.discount.amount[currencyCode] == null
        ) {
            containsError = 'e';
        }
    } else if (coupon.redemption_resource !== 'subscription') {
        containsError = true;
    }
    return containsError;
}

export function onCouponErrored(res) {
    let errorString = '';

    switch (res) {
        case 'a': // Free trial not available to existing subscribers
            errorString = 'recurly_this_promotional_code_is_valid_for_new_subscriptions_only';
            break;
        case 'b': // This coupon has expired // this is currently uncatchable will fallback to default
            errorString = 'recurly_this_promotional_code_has_expired';
            break;
        case 'c': // This one use coupon has already been redeemed
            errorString = 'recurly_this_promotional_code_has_already_been_redeemed';
            break;
        case 'd': // This coupon is not available in your region
            errorString = 'recurly_this_promotional_code_is_not_supported_in_your_country';
            break;
        case 'e': // This coupon is not available in your region for currency regions
            errorString =
                'recurly_this_promotional_code_is_not_in_the_same_currency_as_your_subscription';
            break;
        default:
            //  error all other issues
            errorString = 'recurly_this_promotional_code_is_not_valid';
    }
    return errorString;
}

export function isAarpCoupon(couponCode) {
    if (!couponCode) {
        return false;
    }

    return couponCode.startsWith(FLOW_TYPE.AARP);
}

function getSingleCadenceAmount(temporalUnit, isAnnualPlan) {
    if (isAnnualPlan && temporalUnit === 'year') {
        return 1;
    } else if (isAnnualPlan && temporalUnit === 'month') {
        return 12;
    }

    return isAnnualPlan ? 12 : 1;
}
/**
 * Converts a decimal rate to a percent string.
 * @param {number} decimalRate - The decimal rate to convert.
 * @returns {string} The formatted percent string.
 */
export function convertDecimalRateToPercent(decimalRate) {
    const locale = CBS.Registry.region.locale ?? 'en-US';
    return `${Intl.NumberFormat(locale, { style: 'percent' }).format(decimalRate)}`;
}
